import type { GateContext, Lesson } from '../types';
import { f, finalError, lastSample, run } from './helpers';
import { settlingTime } from '../../simulation/metrics';
import { expandTrace } from '../../state/runRecord';

const TABLE_T = [1.5, 2.0, 2.5, 3.0, 3.5];
const tableSamples = (ctx: GateContext) => TABLE_T.map((t) => ctx.sample('m6-pi', t));

function recoveryTime(ctx: GateContext, stepId: string) {
  const r = run(ctx, stepId);
  return settlingTime(expandTrace(r.trace), { tStart: 10, tEnd: r.config.duration, from: 10, to: 4, tolerance: 0.05 }).value;
}

export const m6: Lesson = {
  id: 'm6',
  number: 6,
  title: 'PI control',
  summary: 'In this module, you will create a controller that can remember past mistakes. See how tracking persistent errors helps the drone reach its exact target altitude, even with heavy payloads, and learn how anti-windup keeps the thrusters from overreacting when pushed to full power.',
  objectives: [
    'Estimate accumulated error from a table of samples.',
    'Explain how the integral term holds a load with near-zero error.',
    'Use a saturation trace to explain windup and anti-windup recovery.',
  ],
  controls: ['controller-kind', 'Kp', 'Ki', 'anti-windup', 'feedforward', 'contributions'],
  controllerKinds: ['p', 'pi'],
  sections: [
    {
      id: 'm6-integral',
      title: 'Accumulated error',
      phase: 'learn',
      blocks: [
        {
          type: 'p',
          text: 'In Module 4, we discovered a key limitation of proportional feedback: whenever a drone carries an unmodeled payload or battles a steady headwind or downdraft, proportional control permanently sags below target. To achieve an accurate height while hovering and eliminate steady-state offsets, flight software must have a **memory of past errors**.',
        },
        {
          type: 'p',
          text: 'The mathematical tool for tracking cumulative error over time is the **{{integral}}** (written $\\int e\\,dt$). In digital flight computers running at discrete time steps $\\Delta t$, the integral is simply calculated as a running sum that accumulates error multiplied by elapsed time:',
        },
        {
          type: 'math',
          tex: '\\term{I}{I_{next}} = \\term{I}{I} + \\term{error}{e}\\,\\term{dt}{\\Delta t}',
          aria: 'The next I equals the current I plus error e times delta t.',
        },
        {
          type: 'list',
          items: [
            '**Accumulated error** ($\\term{I}{I}$): The running total of signed area under the error curve, measured in metre-seconds (m·s).',
            '**Time step** ($\\term{dt}{\\Delta t}$): The discrete sampling interval of the flight computer ($0.005\\text{ s}$ in our simulator).',
          ],
        },
        {
          type: 'p',
          text: 'Units check: $\\text{metres} \\times \\text{seconds} = \\text{m}\\cdot\\text{s}$. Error above target ($e > 0$, vehicle too low) adds positive area to $I$; error below target ($e < 0$, vehicle too high) subtracts area. Notice how duration dominates: an altitude sag of just $0.05\\text{ m}$ lasting for $10\\text{ s}$ accumulates $0.5\\text{ m}\\cdot\\text{s}$, whereas a sharp spike of $0.4\\text{ m}$ lasting only $0.5\\text{ s}$ produces only $0.2\\text{ m}\\cdot\\text{s}$.',
        },
        { type: 'widget', widget: 'integral-area' },
      ],
    },
    {
      id: 'm6-pi',
      title: 'The PI rule',
      phase: 'learn',
      blocks: [
        {
          type: 'p',
          text: 'Pairing immediate reaction (P) with the past memory from the Integral (I) gives us the Proportional-Integral (PI) controller. In robotics and aerospace, PI control is the go-to workhorse for counteracting constant forces like wind or extra weight.',
        },
        {
          type: 'p',
          text: 'The PI feedback rule sums the proportional and integral contributions:',
        },
        {
          type: 'math',
          tex: '\\term{ufb}{u_{fb}} = \\term{Kp}{K_p}\\, \\term{error}{e} + \\term{Ki}{K_i}\\, \\term{I}{I}',
          aria: 'u f b equals K p times error e, plus K i times I.',
        },
        {
          type: 'p',
          text: 'Here is how PI completely cures steady-state sag: when a downward load appears, the drone initially sags ($e > 0$). While proportional thrust $u_P = K_p e$ resists the fall, the integral $I$ begins steadily building up. As $I$ grows, the integral term $u_I = K_i I$ ramps up and takes over the physical burden of supporting the load. Crucially, $u_I$ does not disappear when error reaches zero; it remains frozen at the exact thrust needed to support the payload, allowing the aircraft to settle at **zero steady-state error** ($e = 0$).',
        },
        {
          type: 'callout',
          tone: 'warn',
          title: 'Stability trade-offs with integral gain',
          text: 'Integral control eliminates constant offsets, but it introduces phase lag. Cranking $K_i$ too high causes slow, ringing oscillations and can destabilize the aircraft. Furthermore, integral action can only eliminate errors if the required thrust falls within the physical limits of the motors ($0 \\le T \\le T_{max}$).',
        },
      ],
    },
    {
      id: 'm6-windup',
      title: 'Windup and anti-windup',
      phase: 'learn',
      blocks: [
        {
          type: 'p',
          text: 'Integrator windup is one of the most notorious causes of real-world drone crashes and industrial robot failures. If a flight controller commands the motors to exceed their physical capability, a naive integrator keeps accumulating blindly, leading to catastrophic overshoot when the motors finally catch up.',
        },
        {
          type: 'p',
          text: 'Consider what happens when a target demands more thrust than the motors can produce (e.g. requiring 28 N when $T_{max} = 25\\text{ N}$). The motors {{saturation|saturate}} at maximum power, but the drone cannot climb fast enough to close the gap. While the aircraft struggles, error remains positive and $I$ balloons to a massive value. When the target drops back down to an achievable altitude, the huge accumulated $I$ holds motor thrust pinned at 100% until the integral slowly drains away, causing massive delay and extreme overshoot.',
        },
        {
          type: 'p',
          text: 'The industry-standard solution is **{{anti-windup}} clamping**: the flight computer monitors actuator saturation. If the motors are already pinned at maximum thrust and error is still positive, the software freezes the integral update ($I_{next} = I$). The instant error reverses, integration immediately resumes to unwind the command.',
        },
      ],
    },
    {
      id: 'm6-predict',
      title: 'Predict',
      phase: 'predict',
      blocks: [
        {
          type: 'p',
          text: 'In Module 4, a proportional gain of $K_p = 4\\text{ N/m}$ left a permanent sag of ~0.17 m under a 1 N load. Predict what happens when you add an integral gain of $K_i = 3\\text{ N/(m}\\cdot\\text{s)}$.',
        },
        {
          type: 'predict',
          id: 'm6-p1',
          prompt: 'P with $K_p$ = 4 left 1/6 m of error under a 1 N load. After adding $K_i$ = 3, what will the error be after 20 s?',
          options: [
            { id: 'same', text: 'Still about 0.17 m' },
            { id: 'zero', text: 'Close to zero' },
            { id: 'neg', text: 'It will overshoot and stay above the target' },
          ],
          reveal: 'Because error remains positive initially, the integral sum $I$ grows until the integral force $u_I = K_i I$ supplies the full 1 N needed to support the load. Once $u_I \\approx 1\\text{ N}$, the drone returns to its commanded altitude, driving steady-state error to zero.',
        },
      ],
    },
    {
      id: 'm6-lab',
      title: 'P vs PI, then windup',
      phase: 'experiment',
      blocks: [
        {
          type: 'p',
          text: 'In this lab, you will see how the integral term cures altitude sag under heavy loads, and see firsthand why thrusters need anti-windup protection when pushed to 100% throttle.',
        },
        {
          type: 'h',
          text: 'Part 1: Eliminating Steady-State Sag with Integral Action',
        },
        {
          type: 'p',
          text: '**Purpose**: Pure proportional control ($P$) always sags under a constant external load because it needs an error to generate corrective thrust. By adding an integral term ($u_I = K_i\\,I$), the flight computer accumulates persistent error over time, steadily ramping up motor power until the vehicle returns to exact target altitude ($e = 0$). You will compare pure P ($K_p = 4$) against PI ($K_p = 4, K_i = 3$) under a 1 N downward load.',
        },
        {
          type: 'h',
          text: 'Part 2: Actuator Saturation and Integrator Windup',
        },
        {
          type: 'p',
          text: '**Purpose**: Drones have physical motor limits ($T_{max} = 25\\text{ N}$). When a large target jump demands more thrust than the hardware can supply, the motors saturate at 100%. An unprotected integrator continues accumulating error blindly (\"windup\"), storing a massive command that prevents the craft from slowing down when the target drops. You will test an infeasible 10 m step to observe windup overshoot and verify how anti-windup clamping restores immediate recovery.',
        },
        { type: 'lab' },
      ],
    },
    {
      id: 'm6-inspect',
      title: 'Inspect',
      phase: 'inspect',
      blocks: [
        {
          type: 'p',
          text: 'Analyze the controller contributions and saturation telemetry:',
        },
        {
          type: 'list',
          items: [
            'In the PI load run, expand **Controller contributions**: notice that as altitude returns to 2 m ($e \\to 0$), proportional term $u_P$ shrinks to zero while integral term $u_I$ stabilizes at ~1 N.',
            'Compare the two windup runs: check the value of $u_I$ at $t = 10\\text{ s}$. Notice how many seconds the unprotected controller remains stuck in saturation compared to the anti-windup run.',
          ],
        },
      ],
    },
    {
      id: 'm6-explain',
      title: 'Explain',
      phase: 'explain',
      blocks: [
        { type: 'reflect', id: 'm6-r1', prompt: 'Describe windup to someone who has not seen the plots.' },
      ],
    },
  ],
  lab: {
    id: 'm6-lab',
    version: 1,
    instructions: 'Complete all four 25 s runs: Steps 1–2 test load sag elimination with integral action; Steps 3–4 demonstrate integrator windup and anti-windup clamping during motor saturation.',
    steps: [
      {
        id: 'm6-p',
        title: 'Part 1: P only ($K_p = 4$)',
        presetId: 'pi-load',
        setup: (c) => {
          c.controller.kind = 'p';
        },
        instructions:
          'Load the step ($P$ only) and run to the end (25 s). When the 1 N load appears at 1 s, watch the drone sink and stay permanently drooped at ~1.83 m because proportional thrust alone cannot clear steady-state offset.',
        permitted: [],
        requires: 'A complete 25 s run.',
      },
      {
        id: 'm6-pi',
        title: 'Part 1: PI ($K_p = 4$, $K_i = 3$)',
        presetId: 'pi-load',
        setup: (c) => {
          c.controller.kind = 'pi';
        },
        instructions:
          'Load the step (PI controller) and run to the end (25 s). Watch the integral contribution ($u_I$) ramp up over time, gradually lifting the craft back to 2.0 m to completely eliminate steady-state load error.',
        permitted: [],
        requires: 'A complete 25 s run.',
      },
      {
        id: 'm6-windup-off',
        title: 'Part 2: Infeasible target, anti-windup off',
        presetId: 'windup',
        instructions:
          'Load the step and run to the end (25 s). At 1 s, target jumps to 10 m and thrusters peg at 100% (25 N) as the unconstrained integral balloons. When target drops to 4 m at 10 s, watch the bloated integral hold full throttle, causing severe overshoot up to ~7 m.',
        permitted: [],
        requires: 'A complete 25 s run with anti-windup off.',
      },
      {
        id: 'm6-windup-on',
        title: 'Part 2: Infeasible target, anti-windup on',
        presetId: 'windup',
        setup: (c) => {
          c.controller.antiWindup = true;
        },
        instructions:
          'Load the step with anti-windup enabled and run to the end (25 s). Notice that anti-windup clamps the integrator during saturation; when the target drops to 4 m at 10 s, the controller unlatches immediately, settling cleanly onto 4 m with minimal delay.',
        permitted: [],
        requires: 'A complete 25 s run with anti-windup on.',
      },
    ],
  },
  gate: {
    id: 'g6',
    version: 1,
    title: 'Check: integral action',
    questions: [
      {
        id: 'g6-q1',
        version: 1,
        kind: 'numeric',
        binds: ['m6-pi'],
        prompt: () =>
          'Here are five error samples from your PI run, 0.5 s apart. Estimate how much error they accumulate. Multiply each error by 0.5 s and add the five results.',
        table: (ctx) => ({
          caption: 'Error samples',
          headers: ['t (s)', 'e (m)'],
          rows: tableSamples(ctx).map((s) => [f(s.t), f(s.e, 4)]),
        }),
        unit: 'm·s',
        answer: (ctx) => tableSamples(ctx).reduce((a, s) => a + Number(f(s.e, 4)) * 0.5, 0),
        tolerance: 0.002,
        misconceptions: [
          { test: (x, ctx) => Math.abs(x - tableSamples(ctx).reduce((a, s) => a + Number(f(s.e, 4)), 0)) < 0.002, feedback: 'Multiply each error by the 0.5 s interval before adding.' },
        ],
        hint: 'Area of each thin rectangle = height (error) × width (0.5 s).',
        explanation: 'This rectangle sum is exactly what the controller does each step, with Δt = 0.005 s instead of 0.5 s.',
        review: 'm6-integral',
      },
      {
        id: 'g6-q2',
        version: 1,
        kind: 'choice',
        binds: ['m6-pi'],
        prompt: (ctx) => {
          const s = lastSample(ctx, 'm6-pi');
          return `At the end of your PI run: e = ${f(s.e, 4)} m, $u_P$ = ${f(s.uP, 3)} N, $u_I$ = ${f(s.uI, 3)} N. What is holding up the 1 N load?`;
        },
        options: [
          { id: 'I', text: 'The integral term $u_I$, built up from past error.' },
          { id: 'P', text: 'The proportional term $u_P$.', feedback: 'With near-zero error, $u_P = K_p e$ is near zero.' },
          { id: 'ff', text: 'Feedforward was updated to include the load.', feedback: '$T_{ff}$ stayed at $T_0$ the whole time.' },
          { id: 'spring', text: 'The spring.', feedback: 'The vehicle is back near $z_s$, where the spring exerts almost no force.' },
        ],
        correct: () => 'I',
        hint: 'Look at which contribution is about 1 N.',
        explanation: 'The integral remembers the past error and keeps supplying the force after the error is gone.',
        review: 'm6-pi',
      },
      {
        id: 'g6-q3',
        version: 1,
        kind: 'numeric',
        binds: ['m6-p', 'm6-pi'],
        prompt: () => 'Using the final 2 s of each run, how much smaller is the mean error with PI than with P? (P error − PI error)',
        table: (ctx) => ({
          caption: 'Final-window mean error',
          headers: ['Run', 'mean e (m)'],
          rows: [
            ['P', f(finalError(run(ctx, 'm6-p')), 4)],
            ['PI', f(finalError(run(ctx, 'm6-pi')), 4)],
          ],
        }),
        unit: 'm',
        answer: (ctx) => Number(f(finalError(run(ctx, 'm6-p')), 4)) - Number(f(finalError(run(ctx, 'm6-pi')), 4)),
        tolerance: 0.002,
        hint: 'Subtract the PI value from the P value.',
        explanation: 'PI removed essentially the whole 1/6 m bias.',
      },
      {
        id: 'g6-q4',
        version: 1,
        kind: 'choice',
        binds: ['m6-windup-off', 'm6-windup-on'],
        prompt: (ctx) => {
          const Ioff = ctx.sample('m6-windup-off', 10).I;
          const Ion = ctx.sample('m6-windup-on', 10).I;
          const rOff = recoveryTime(ctx, 'm6-windup-off');
          const rOn = recoveryTime(ctx, 'm6-windup-on');
          const rt = (x: number | null) => (x === null ? 'not settled' : `${f(x, 1)} s`);
          return `At t = 10 s the accumulated error was I = ${f(Ioff)} m·s without anti-windup and ${f(Ion)} m·s with it. Settling to 4 m took ${rt(rOff)} vs ${rt(rOn)}. Why did the run without anti-windup take longer?`;
        },
        options: [
          { id: 'wound', text: 'While thrust was saturated, the integral kept growing. After the target dropped it had to unwind before thrust fell.' },
          { id: 'kp', text: 'Its $K_p$ was higher.', feedback: 'Both runs used the same gains. Only anti-windup differs.' },
          { id: 'tmax', text: 'Its maximum thrust was lower.', feedback: 'Both had $T_{max}$ = 25 N.' },
          { id: 'noise', text: 'Sensor noise.', feedback: 'Noise is off in both runs.' },
        ],
        correct: () => 'wound',
        hint: 'Compare I at 10 s and the thrust plot just after 10 s.',
        explanation: 'Anti-windup stops the integral piling up while the actuator can’t respond, so recovery is quicker.',
        review: 'm6-windup',
      },
    ],
  },
};
